<template>
    <skeleton v-if="isLoading" />
    <form
        v-else
        @submit="onSubmit"
    >
        <content-reset :body="formMarkup" />

        <div
            v-if="activeFacilityCount > 1"
            class="mt-6 flex flex-col"
        >
            <label
                for="select_facility"
                class="font-semibold"
            >
                {{ facilityLabel }}
            </label>
            <span class="w-full">
                <facility-select
                    id="select_facility"
                    v-model="facilityId"
                    :facility-groups="facilityGroups"
                    :invalid="errors.facilityId !== undefined"
                />
            </span>
            <span
                id="facility-error"
                class="text-small text-red-600"
            >
                {{ errors.facilityId || '&nbsp;' }}
            </span>
        </div>

        <div class="mt-2">
            <label class="font-semibold">{{ t('customer') }}</label>
            <span class="w-full">
                <input-text
                    :model-value="viewer?.name"
                    disabled
                    class="w-full"
                    :placeholder="t('customer')"
                    maxlength="255"
                />
            </span>
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">{{
                    t('customerInformation.emailAddress')
                }}</label>
                <span
                    v-show="errors.email"
                    id="email-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.email }}
                </span>
            </div>
            <span class="p-input-icon-left w-full">
                <i class="pi pi-envelope"></i>
                <input-text
                    id="email"
                    v-model="emailValue"
                    v-bind="emailProps"
                    inputmode="email"
                    type="email"
                    class="w-full !text-[16px]"
                    :pt="{
                        root: {
                            class: errors.email
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    :placeholder="t('customerInformation.emailAddress')"
                    aria-describedby="email-error"
                />
            </span>
            <span class="text-sm text-gray-500">{{
                t('contactUsEmailHelp')
            }}</span>
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">{{ t('subject') }}</label>
                <span
                    v-show="errors.subject"
                    id="email-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.subject }}
                </span>
            </div>
            <span class="w-full">
                <input-text
                    id="subject"
                    v-model="subject"
                    v-bind="subjectProps"
                    class="w-full !text-[16px]"
                    :placeholder="t('subject')"
                    maxlength="255"
                    autofocus
                    :pt="{
                        root: {
                            class: errors.subject
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
            </span>
        </div>

        <div class="mt-6">
            <div>
                <label class="font-semibold">{{ t('message') }}</label>
                <span
                    v-show="errors.message"
                    id="message-error"
                    class="text-small text-red-600"
                >
                    &mdash; {{ errors.message }}
                </span>
            </div>
            <span class="w-full">
                <prime-textarea
                    id="message"
                    v-model="message"
                    v-bind="messageProps"
                    class="w-full"
                    :placeholder="t('message')"
                    :rows="5"
                    :cols="30"
                    maxlength="1000"
                    :pt="{
                        root: {
                            class: errors.message
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
            </span>
        </div>

        <div class="mt-4 flex justify-end">
            <prime-button
                type="submit"
                :label="t('submit')"
                :loading="isPending"
            />
        </div>
    </form>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import ContentReset from '~/components/ContentReset.vue'
import PrimeTextarea from 'primevue/textarea'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, toRef, watch } from 'vue'
import { useViewer, useSetViewer } from '~/composables/use-login'
import { toTypedSchema } from '@vee-validate/yup'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import { useToast } from 'primevue/usetoast'
import { useCreateTicketMutation } from '~/components/Tickets/use-create-ticket-mutation'
import { TicketConfirmationTicketFragment } from '~/components/Tickets/TicketConfirmation.vue'
import Skeleton from 'primevue/skeleton'
import FacilitySelect from '~/components/Verification/FacilitySelect.vue'
import { useI18n } from 'vue-i18n'
import { graphql } from '~/resources/graphql'

const props = defineProps<{
    formMarkup: string
    // If this props is not provided, a facility selector will be shown
    facilityIdContext?: string
}>()

const emit = defineEmits<{
    (e: 'ticketCreated', payload: typeof TicketConfirmationTicketFragment): void
}>()

const { t } = useI18n({ useScope: 'global' })
const facilityIdContextRef = toRef(props, 'facilityIdContext')

const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 100 facilities
const { data, isLoading } = useQuery({
    queryKey: ['LoggedInContactUsTicketFormQuery', facilityIdContextRef],
    placeholderData: previousData => previousData,
    queryFn: () => {
        // If a facility has been provided, no need for this query
        if (facilityIdContextRef.value !== undefined) {
            return null
        }

        return query(
            graphql(/* GraphQL */ `
                query LoggedInContactUsTicketFormQuery {
                    organization {
                        facilityLocationGroups {
                            id
                            name
                            facilities {
                                id
                                label
                            }
                        }
                    }
                }
            `)
        )
    }
})

const facilityGroups = computed(() => {
    // If a facility has been provided, no need for this
    if (props.facilityIdContext !== undefined) {
        return []
    }

    return [
        ...(data.value?.organization.facilityLocationGroups ?? []).map(
            group => ({
                id: group.id,
                name: group.name,
                facilities: group.facilities
            })
        )
    ]
})

const activeFacilities = computed(() =>
    facilityGroups.value.flatMap(group => group.facilities)
)

const activeFacilityCount = computed(() => activeFacilities.value?.length ?? 0)
const facilityLabel = computed(() => t('facility', 1))

const viewer = useViewer()

// Keep all contact form validation copy in the shared locale file so both
// contact flows stay in sync and translators only touch one source of truth.
const schema = toTypedSchema(
    object({
        email: string()
            .email(t('customerInformation.invalidEmailWarning'))
            .required(t('emailRequired')),
        subject: string().max(255).required(t('subjectRequired')),
        message: string().max(1000).required(t('messageRequired')),
        facilityId: string().required(t('signInFlow.facilityRequired'))
    })
)

const { errors, defineField, handleSubmit } = useForm({
    validationSchema: schema,
    initialValues: {
        email: viewer.value?.customerEmail ?? ''
    }
})

const [emailValue, emailProps] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

const [subject, subjectProps] = defineField('subject', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [message, messageProps] = defineField('message', {
    validateOnModelUpdate: false,
    validateOnBlur: false
})

const [facilityId] = defineField('facilityId')

// This is for handling the 1 facility case
watch(activeFacilities, newValue => {
    if (newValue === null) {
        return
    }

    if (newValue.length === 1) {
        facilityId.value = newValue[0].id
    }
})

// This is for handling the case where a facility has been provided
onMounted(() => {
    if (props.facilityIdContext !== undefined) {
        facilityId.value = props.facilityIdContext
    }
})

const toast = useToast()
const setViewer = useSetViewer()
const onSubmit = handleSubmit(values => {
    // Refresh the viewer after submit because changing the email field also
    // updates the persisted customer email on the account.
    mutate(
        {
            email: values.email,
            subject: values.subject,
            message: values.message,
            facilityId: values.facilityId
        },
        {
            onSuccess: data => {
                // Update viewer because email has changed
                setViewer(data.query.viewer)
                emit('ticketCreated', data.record)
                toast.add({
                    severity: 'success',
                    summary: t('success'),
                    detail: t('contactUsTicketCreated'),
                    life: 3000,
                    closable: false
                })
            }
        }
    )
})

const { mutate, isPending } = useCreateTicketMutation()
</script>
